import { useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components/macro'

import {
  Dialog,
  Layout,
  Radio,
  RadioGroup,
  Text,
  type RadioGroupChangeEventDetail,
} from '@loadsmart/miranda-react'
import { getToken } from '@loadsmart/miranda-react/dist/tokens'

import { useConfigContext } from 'config/ui/ConfigContext'

import { formatTenderRejectText } from '../../presenter/tenderPresenter'

const CustomRadio = styled(Radio)`
  --m-radio-label-color: ${getToken('color-text-primary')};
`

const radioValues = [
  'I need another price',
  "I don't have capacity available",
  'The pickup date is too close',
  'Other',
] as const

type RadioValue = (typeof radioValues)[number]

export type TenderRejectDialogProps = {
  loading?: boolean
  onCancel: () => void
  onConfirm: (rejectReason: string) => void
  open?: boolean
}

export function TenderRejectDialog({
  loading,
  onCancel,
  onConfirm,
  open = false,
}: TenderRejectDialogProps) {
  const enableTenderRejectReason = useConfigContext().enableTenderRejectReason.toBoolean()
  const [rejectReason, setRejectReason] = useState<RadioValue>(radioValues[0])

  function handleClick(event: React.MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
  }

  function handleOnChange({ detail }: CustomEvent<RadioGroupChangeEventDetail>) {
    setRejectReason(detail.value as RadioValue)
  }

  function handleConfirm() {
    if (!loading) {
      onConfirm(rejectReason)
    }
  }

  function handleCancel() {
    if (!loading) {
      onCancel()
    }
  }

  return createPortal(
    <Dialog
      data-testid="dialog-tender-reject"
      open={open}
      onClose={handleCancel}
      onClick={handleClick}
    >
      <Dialog.Header>
        Reject tender
        <Dialog.Close onClick={handleCancel} disabled={loading} />
      </Dialog.Header>
      <Dialog.Body>
        <Layout.Stack>
          <Text variant="body-md">{formatTenderRejectText(enableTenderRejectReason)}</Text>
          {enableTenderRejectReason && (
            <RadioGroup onChange={handleOnChange}>
              {radioValues.map(value => (
                <CustomRadio value={value} key={value}>
                  {value}
                </CustomRadio>
              ))}
            </RadioGroup>
          )}
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionTertiary onClick={handleCancel} loading={loading}>
          Cancel
        </Dialog.ActionTertiary>
        <Dialog.ActionPrimary onClick={handleConfirm} loading={loading}>
          Reject tender
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>,
    document.body
  )
}
